import useAxios from 'axios-hooks';
import React, { useState, useEffect } from 'react';
import { Stack } from '@fluentui/react';
import Select from "react-select";
import { FixedSizeList as List } from "react-window";

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const SelectCobrandsForm = ({ width = 400, onCobrandsSelected }) => {
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedCobrandTag, setSelectedCobrandTag] = useState(null);
    const [selectedCobrand, setSelectedCobrand] = useState(null);

    // API calls for fetching data
    const [{ data: clientsData, loading: loadingClients }] = useAxios({
        url: 'internal/clients',
        method: 'GET',
    });

    const [{ data: cobrandTagsData, loading: loadingCobrandTags }] = useAxios({
        url: 'internal/tags',
        method: 'GET',
    });

    const [{ data: cobrandsData, loading: loadingCobrands }] = useAxios({
        url: 'internal/cobrands',
        method: 'GET',
    });

    const [{ data: cobrandIdsData, loading: fetchingCobrandIds }, getCobrandIds] = useAxios(
        { url: 'internal/cobrand/ids-from-tags', method: 'POST' },
        { manual: true }
    );

    // Format data for dropdowns
    const clientOptions = clientsData?.map(client => ({
        value: client.clientID,
        label: client.name,
    })) || [];

    const cobrandTagOptions = cobrandTagsData?.map(tag => ({
        value: tag.coBrandTagID,
        label: tag.name,
    })) || [];

    const cobrandOptions = cobrandsData?.map(cobrand => ({
        value: cobrand.cobrandId,
        label: cobrand.name,
    })) || [];

    // Handle selection and fetch cobrands
    const handleSelectionChange = async (selectionType, selectedValue, selectedLabel) => {
        // Clear other selections when one is made
        if (selectionType === 'client') {
            setSelectedClient(selectedValue);
            setSelectedCobrandTag(null);
            setSelectedCobrand(null);
        } else if (selectionType === 'cobrandTag') {
            setSelectedCobrandTag(selectedValue);
            setSelectedClient(null);
            setSelectedCobrand(null);
        } else if (selectionType === 'cobrand') {
            setSelectedCobrand(selectedValue);
            setSelectedClient(null);
            setSelectedCobrandTag(null);
        }

        // Fetch cobrands based on selection
        const getCobrandIdsData = {
            client: selectionType === 'client' ? [selectedValue] : null,
            cobrandTag: selectionType === 'cobrandTag' ? [selectedValue] : null,
            cobrand: selectionType === 'cobrand' ? [selectedValue] : null,
        };

        try {
            const response = await getCobrandIds({
                data: getCobrandIdsData,
            });

            if (response.data && response.data.length > 0) {
                // Sort the data and pass to parent with selection metadata
                const sortedCobrands = response.data.sort((a, b) => a.name.localeCompare(b.name));
                if (onCobrandsSelected) {
                    onCobrandsSelected(sortedCobrands, {
                        type: selectionType,
                        name: selectedLabel,
                    });
                }
            }
        } catch (err) {
            if (err?.constructor?.name === 'Cancel') {
                console.warn("Cobrand ID request was cancelled.");
            } else {
                console.error("Failed to fetch cobrand IDs", err);
            }
        }

        // Clear the selection after a delay to allow user to see the selection
        setTimeout(() => {
            setSelectedClient(null);
            setSelectedCobrandTag(null);
            setSelectedCobrand(null);
        }, 1000); // Increased delay to 1 second
    };

    const handleClientChange = (option) => {
        if (option) {
            handleSelectionChange('client', option.value, option.label);
        }
    };

    const handleCobrandTagChange = (option) => {
        if (option) {
            handleSelectionChange('cobrandTag', option.value, option.label);
        }
    };

    const handleCobrandChange = (option) => {
        if (option) {
            handleSelectionChange('cobrand', option.value, option.label);
        }
    };

    return (
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { width: '100%' } }}>
            <Select
                components={{ MenuList: CustomMenuList }}
                placeholder="Select a Client"
                options={clientOptions}
                value={clientOptions.find(opt => opt.value === selectedClient)}
                onChange={handleClientChange}
                isDisabled={loadingClients}
                isSearchable
                styles={{
                    menuList: (base) => ({
                        ...base,
                        maxHeight: "300px",
                        overflowY: "auto",
                    }),
                    container: (base) => ({
                        ...base,
                        width: '100%',
                    }),
                    control: (base) => ({
                        ...base,
                        border: "1px solid #aaaaaa",
                    }),
                }}
            />
            <Select
                components={{ MenuList: CustomMenuList }}
                placeholder="Select a Cobrand Tag"
                options={cobrandTagOptions}
                value={cobrandTagOptions.find(opt => opt.value === selectedCobrandTag)}
                onChange={handleCobrandTagChange}
                isDisabled={loadingCobrandTags}
                isSearchable
                styles={{
                    menuList: (base) => ({
                        ...base,
                        maxHeight: "300px",
                        overflowY: "auto",
                    }),
                    container: (base) => ({
                        ...base,
                        width: '100%',
                    }),
                    control: (base) => ({
                        ...base,
                        border: "1px solid #aaaaaa",
                    }),
                }}
            />
            <Select
                components={{ MenuList: CustomMenuList }}
                placeholder="Select a Cobrand"
                options={cobrandOptions}
                value={cobrandOptions.find(opt => opt.value === selectedCobrand)}
                onChange={handleCobrandChange}
                isDisabled={loadingCobrands}
                isSearchable
                styles={{
                    menuList: (base) => ({
                        ...base,
                        maxHeight: "300px",
                        overflowY: "auto",
                    }),
                    container: (base) => ({
                        ...base,
                        width: '100%',
                    }),
                    control: (base) => ({
                        ...base,
                        border: "1px solid #aaaaaa",
                    }),
                }}
            />
        </Stack>
    );
};

export default SelectCobrandsForm;